import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { History } from 'lucide-react';
import type { ActivityLogToken } from '@sleed/bizcommerce-api';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { SystemGrid } from '~/components/system/grid/system-grid';
import { activityLogFields, useActivityLogList } from '~/hooks/use-activity-log';
import { useUserRolesList } from '~/hooks/use-user-roles';
import type { SortDir } from '~/lib/grid/sort';
import { ACTION_LABEL_KEYS, useActivityLogColumns } from './columns';

const SEARCH_DEBOUNCE_MS = 250;

/**
 * Rolling windows, in days, for the quick date filters. An audit log is nearly always read by recency, and a
 * date range is clumsy to express through a generic filter — so the windows anyone actually wants are one
 * click. Rolling rather than calendar-based: no midnight boundary to reconcile with the UTC timestamps.
 */
const QUICK_RANGES = [1, 7, 30] as const;

/**
 * Admin activity log (BW-4412) — read-only, server-paged grid: who did what, with which role, on which
 * entity. Server-paged rather than client-paged because the table grows without bound.
 */
export function ActivityLogGridView() {
	const { t } = useTranslation();

	const [pageIndex, setPageIndex] = useState(0);
	const [pageSize, setPageSize] = useState(25);
	const [sort, setSort] = useState<{ key: string; dir: SortDir } | null>({ key: activityLogFields.occurredOn, dir: 'desc' });
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [action, setAction] = useState<number | null>(null);
	const [actorRoleId, setActorRoleId] = useState<number | null>(null);
	// The cutoff is stamped once, when the button is pressed, and held alongside the window it came from.
	// Deriving it from Date.now() on each render would produce a new query key every render.
	const [quickRange, setQuickRange] = useState<{ days: number; fromDateUtc: string } | null>(null);

	// Debounced search: every keystroke would otherwise be a round-trip.
	useEffect(() => {
		if (searchInput === search) return;
		const id = setTimeout(() => {
			setSearch(searchInput);
			setPageIndex(0);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(id);
	}, [searchInput, search]);

	// The role dropdown is populated from the roles themselves, not from the log: a role with no
	// activity yet must still be selectable, and a renamed role must not appear twice.
	const { data: roles } = useUserRolesList();

	const { data, isLoading, isError, refetch } = useActivityLogList({
		skip: pageIndex * pageSize,
		take: pageSize,
		search,
		action,
		actorRoleId,
		fromDateUtc: quickRange?.fromDateUtc ?? null,
		sortSelector: sort?.key ?? null,
		sortDir: sort?.dir ?? 'desc'
	});

	const columns = useActivityLogColumns();

	// Pressing the active window again clears it, so the buttons work as a toggle group with no separate
	// "all" control to keep in sync.
	const toggleQuickRange = (days: number) => {
		setQuickRange(current =>
			current?.days === days ? null : { days, fromDateUtc: new Date(Date.now() - days * 86400 * 1000).toISOString() }
		);
		setPageIndex(0);
	};

	const toolbarExtras = (
		<>
			<div className="inline-flex rounded-md border border-border p-0.5" role="group" aria-label={t('system.activityLog.filters.period')}>
				{QUICK_RANGES.map(days => (
					<Button
						key={days}
						variant={quickRange?.days === days ? 'default' : 'ghost'}
						size="sm"
						aria-pressed={quickRange?.days === days}
						onClick={() => toggleQuickRange(days)}>
						{t(`system.activityLog.filters.last${days}`)}
					</Button>
				))}
			</div>
			<Select
				value={action === null ? 'all' : String(action)}
				onValueChange={value => {
					setAction(value === 'all' ? null : Number(value));
					setPageIndex(0);
				}}>
				<SelectTrigger className="h-9 w-44" aria-label={t('system.activityLog.filters.action')}>
					<SelectValue placeholder={t('system.activityLog.filters.action')} />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">{t('system.activityLog.filters.allActions')}</SelectItem>
					{Object.entries(ACTION_LABEL_KEYS).map(([value, key]) => (
						<SelectItem key={value} value={value}>
							{t(`system.activityLog.actions.${key}`)}
						</SelectItem>
					))}
				</SelectContent>
			</Select>
			{/* Filters on ActorRoleId and displays ActorRoleName, so a rename neither breaks the filter
			    nor splits one role's history in two. */}
			<Select
				value={actorRoleId === null ? 'all' : String(actorRoleId)}
				onValueChange={value => {
					setActorRoleId(value === 'all' ? null : Number(value));
					setPageIndex(0);
				}}>
				<SelectTrigger className="h-9 w-44" aria-label={t('system.activityLog.filters.role')}>
					<SelectValue placeholder={t('system.activityLog.filters.role')} />
				</SelectTrigger>
				<SelectContent>
					<SelectItem value="all">{t('system.activityLog.filters.allRoles')}</SelectItem>
					{(roles ?? [])
						.filter(role => role.id !== undefined)
						.map(role => (
							<SelectItem key={role.id} value={String(role.id)}>
								{role.nm}
							</SelectItem>
						))}
				</SelectContent>
			</Select>
		</>
	);

	return (
		<SystemGrid<ActivityLogToken>
			gridId="activity-log"
			title={t('system.activityLog.title')}
			rows={data?.items ?? []}
			loading={isLoading}
			error={isError}
			onRetry={() => void refetch()}
			getRowId={a => a.id ?? `${a.occ}-${a.enm}-${a.eid}`}
			columns={columns}
			server={{
				totalCount: data?.totalCount ?? 0,
				pageIndex,
				pageSize,
				onPageChange: setPageIndex,
				onPageSizeChange: size => {
					setPageSize(size);
					setPageIndex(0);
				},
				sort,
				onSortChange: next => {
					setSort(next);
					setPageIndex(0);
				},
				search: searchInput,
				onSearchChange: setSearchInput
			}}
			toolbarExtras={toolbarExtras}
			emptyIcon={<History size={36} className="text-foreground-disabled" strokeWidth={1.25} />}
			exportFilename="activity-log"
		/>
	);
}
